import React, { useState, useEffect, useRef } from 'react';
import ReactPlayer from 'react-player';
import './SequentialVideoPlayer.scss';
import videoBg from '../../assets/axoncare/videobg.png';
import arrowLeft from '../../assets/home/arrow-left.png';
import arrowRight from '../../assets/home/arrow-right.png';

// Helper to extract YouTube ID
const getYouTubeId = (url) => {
  if (!url) return null;
  
  // Special case for YouTube Shorts format
  if (url.includes('/shorts/')) {
    const shortsMatch = url.match(/\/shorts\/([^/?&]+)/);
    return shortsMatch ? shortsMatch[1] : null;
  }
  
  // Regular YouTube URL formats
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

const SequentialVideoPlayer = ({ videoIds, onLastVideoEnd }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playing, setPlaying] = useState(true); // Autoplay first video
  const [loading, setLoading] = useState(false);
  const [dimensions, setDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800
  });
  const playerRef = useRef(null);
  const containerRef = useRef(null);

  // Add resize listener to handle responsive behavior
  useEffect(() => {
    // Set initial dimensions once mounted
    setDimensions({
      width: window.innerWidth,
      height: window.innerHeight
    });
    
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Reset playing state when videoIds change
    if (videoIds && videoIds.length > 0) {
      setCurrentIndex(0);
      setPlaying(true);
    } else {
      setCurrentIndex(0);
      setPlaying(false);
    }
  }, [videoIds]);

  const handleVideoEnd = () => {
    setLoading(true);
    const nextIndex = (currentIndex + 1) % videoIds.length;
    setTimeout(() => {
      setCurrentIndex(nextIndex);
      setPlaying(true); // Continue playing next video
      setLoading(false);
      if (currentIndex === videoIds.length - 1 && onLastVideoEnd) {
        onLastVideoEnd();
      }
    }, 500);
  };

  const handleReady = () => {
    setLoading(false);
  };

  const playNext = () => {
    if (!videoIds || videoIds.length === 0) return;
    setLoading(true);
    const nextIndex = (currentIndex + 1) % videoIds.length;
    setTimeout(() => {
      setCurrentIndex(nextIndex);
      setPlaying(true);
      setLoading(false);
    }, 200);
  };

  const playPrev = () => {
    if (!videoIds || videoIds.length === 0) return;
    setLoading(true);
    const prevIndex = (currentIndex - 1 + videoIds.length) % videoIds.length;
    setTimeout(() => {
      setCurrentIndex(prevIndex);
      setPlaying(true);
      setLoading(false);
    }, 200);
  };

  const selectVideo = (index) => {
    if (index === currentIndex) {
        setPlaying(!playing);
    } else {
        setLoading(true);
        setTimeout(() => {
            setCurrentIndex(index);
            setPlaying(true);
            setLoading(false);
        }, 200);
    }
  };

  if (!videoIds || videoIds.length === 0) {
    return <div className="sequential-video-player-no-videos">No videos to display.</div>;
  }
  
  const currentVideoUrl = videoIds[currentIndex];
  
  // Determine if this is a small screen
  const isSmallScreen = dimensions.width <= 768;

  return (
    <div className="sequential-video-player-container" ref={containerRef}>
      <div className="player-with-arrows">
        <button onClick={playPrev} className="nav-arrow prev-arrow" aria-label="Previous video">
          <img src={arrowLeft} alt="Previous" className="arrow-img" />
        </button>
        
        <div className="mobile-phone-view">
          <div className="phone-screen">
            {currentVideoUrl && (
              <ReactPlayer
                ref={playerRef}
                url={currentVideoUrl}
                className="react-player"
                width="100%"
                height="100%"
                playing={playing}
                controls={isSmallScreen} // Enable controls on small screens
                onEnded={handleVideoEnd}
                onReady={handleReady}
                onBuffer={() => setLoading(true)}
                onBufferEnd={() => setLoading(false)}
                onError={(e) => {
                  console.error("Player error:", e);
                  setLoading(false);
                }}
                config={{
                  youtube: {
                    playerVars: {
                      modestbranding: 1,
                      rel: 0,
                      controls: isSmallScreen ? 1 : 0,
                      showinfo: 0
                    }
                  },
                  file: {
                    attributes: {
                      controlsList: 'nodownload'
                    }
                  }
                }}
              />
            )}
            {loading && (
              <div className="loading-overlay">
                <div className="loader"></div>
              </div>
            )}
          </div>
        </div>
        
        <button onClick={playNext} className="nav-arrow next-arrow" aria-label="Next video">
          <img src={arrowRight} alt="Next" className="arrow-img" />
        </button>
      </div>

      <div className="thumbnail-previews">
        {videoIds.map((videoUrl, index) => {
          const ytId = getYouTubeId(videoUrl);
          const thumbnailUrl = ytId ? `https://i.ytimg.com/vi/${ytId}/mqdefault.jpg` : 'default_thumbnail.png'; 
          return (
            <div
              key={ytId || index}
              className={`thumbnail-item ${index === currentIndex ? 'active' : ''}`}
              onClick={() => selectVideo(index)}
            >
              <img
                src={thumbnailUrl}
                alt={`Thumbnail for video ${index + 1}`}
                onError={(e) => {
                  // If the standard thumbnail fails, try different format or use fallback
                  if (ytId && e.target.src.includes('mqdefault.jpg')) {
                    e.target.src = `https://i.ytimg.com/vi/${ytId}/0.jpg`;
                  } else {
                    e.target.src = 'https://via.placeholder.com/120x68?text=Video';
                  }
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SequentialVideoPlayer; 